import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faTimes, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import useConversations from '../../hooks/useConversations';
import { useMessagesUI } from '../../contexts/MessagesUIContext';
import CustomSelect from '../widgets/CustomSelect';
import {
  fetchOnlineUserIds,
  buildRecipientOptions,
  formatRecipientOptionLabel,
} from './recipientOptions';
import '../../styles/PatientMessaging.css';

const ObjectMessagingModal = ({
  patient,
  admission = null,
  placement = 'patient-card',
  objectType: objectTypeProp,
  objectId: objectIdProp,
  buttonClassName = 'patient-messaging-btn',
  buttonTitle,
  showButton = true,
  isOpen: controlledOpen,
  onClose: controlledOnClose,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [message, setMessage] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const { hasPermission } = useAuth();
  const { sendMessage, fetchEligibleRecipients } = useConversations();
  const { openMessages } = useMessagesUI();

  const canSend = hasPermission ? hasPermission('/messaging/send', 'Execute') : false;
  const canView = hasPermission ? hasPermission('/messaging', 'View') : false;
  const isModalOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const objectType = objectTypeProp || (placement === 'admission-card' ? 'admission' : 'patient');
  const objectId = objectIdProp
    || (placement === 'admission-card' && admission ? admission.admission_id : patient?.patient_id);

  useEffect(() => {
    if (isModalOpen && canSend) {
      loadRecipients();
    }
  }, [isModalOpen, canSend]);

  const loadRecipients = async () => {
    try {
      setIsLoadingUsers(true);
        const [recipientData, onlineIds] = await Promise.all([
          fetchEligibleRecipients(),
          fetchOnlineUserIds(),
        ]);
        setAvailableUsers(buildRecipientOptions(recipientData.recipients || [], onlineIds));
    } catch (err) {
      console.error('Error fetching recipients:', err);
      alert('Failed to load users. Please try again.');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const closeModal = () => {
    setMessage('');
    setSelectedUser('');
    if (controlledOnClose) {
      controlledOnClose();
    } else {
      setInternalOpen(false);
    }
  };

  const openModal = (e) => {
    e?.stopPropagation?.();
    if (controlledOpen === undefined) {
      setInternalOpen(true);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      alert('Please select a user to send the message to.');
      return;
    }
    if (!message.trim()) {
      alert('Please enter a message.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await sendMessage({
        recipientId: parseInt(selectedUser, 10),
        objectType,
        objectId,
        body: message,
      });
      closeModal();
      if (result.conversation?.id) {
        openMessages({ conversationId: result.conversation.id });
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert(err.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayText = () => {
    if (placement === 'admission-card' || objectType === 'admission') {
      return {
        buttonTitle: buttonTitle || 'Task about this admission',
        modalTitle: 'Task About Admission',
        infoTitle: 'Admission Information',
      };
    }
    if (objectType === 'bill') {
      return {
        buttonTitle: buttonTitle || 'Task about this bill',
        modalTitle: 'Task About Bill',
        infoTitle: 'Bill Information',
      };
    }
    return {
      buttonTitle: buttonTitle || 'Task about this patient',
      modalTitle: 'Task About Patient',
      infoTitle: 'Patient Information',
    };
  };

  const displayText = getDisplayText();

  if (!patient || !canView || !canSend) {
    return null;
  }

  return (
    <>
      {showButton && (
        <button
          className={buttonClassName}
          onClick={openModal}
          title={displayText.buttonTitle}
          type="button"
        >
          <FontAwesomeIcon icon={faComment} />
        </button>
      )}

      {isModalOpen && (
        <div className="patient-messaging-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="patient-messaging-modal">
            <div className="patient-messaging-header">
              <h3>{displayText.modalTitle}</h3>
              <button
                className="patient-messaging-close"
                onClick={closeModal}
                aria-label="Close"
                type="button"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="patient-messaging-content">
              <div className="patient-info-section">
                <h4>{displayText.infoTitle}</h4>
                <p><strong>Name:</strong> {patient.patient_first_name} {patient.patient_last_name}</p>
                <p><strong>Patient Number:</strong> {patient.patient_number}</p>
                {patient.patient_dob && <p><strong>DOB:</strong> {patient.patient_dob}</p>}
                {(placement === 'admission-card' || objectType === 'admission') && admission && (
                  <>
                    <p><strong>Admission ID:</strong> {admission.admission_id}</p>
                    {admission.ward && <p><strong>Ward:</strong> {admission.ward}</p>}
                    {admission.branch && <p><strong>Branch:</strong> {admission.branch}</p>}
                  </>
                )}
              </div>

              <form onSubmit={handleSendMessage} className="messaging-form">
                <div className="form-group">
                  <label htmlFor="user-select">Send to:</label>
                  {isLoadingUsers ? (
                    <p>Loading users...</p>
                  ) : (
                    <CustomSelect
                      name="user-select"
                      value={selectedUser}
                      onChange={(name, value) => setSelectedUser(value)}
                      options={availableUsers}
                      placeholder="Search Role - Name..."
                      isClearable
                      isSearchable
                      disabled={isLoadingUsers}
                      formatOptionLabel={formatRecipientOptionLabel}
                    />
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="message-text">Message:</label>
                  <textarea
                    id="message-text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows="4"
                    required
                  />
                </div>

                <div className="messaging-actions">
                  <button type="button" onClick={closeModal} className="btn-cancel">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || isLoadingUsers || !selectedUser}
                    className="btn-send"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                    {isLoading ? ' Sending...' : ' Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ObjectMessagingModal;
